import { randomBytes } from 'crypto';

export interface ResumeSharePayload {
  enabled: boolean;
  path: string | null;
}

export const SHARED_RESUME_PATH_PREFIX = '/resumes/shared';

export function buildSharedResumePath(sharedIdentifier: string): string {
  return `${SHARED_RESUME_PATH_PREFIX}/${encodeURIComponent(sharedIdentifier)}`;
}

/**
 * A `shared_identifier` value: 24 random bytes (192 bits) as base64url. Used
 * both for a resume an authenticated user opts into sharing, and as the sole
 * access token for an anonymous (guest) resume — see
 * `app/api/resume-builder/anonymous/route.ts`. Long enough that guessing or
 * enumerating one is infeasible.
 */
export function createSharedIdentifier(): string {
  return randomBytes(24).toString('base64url');
}

/**
 * Generates a `shared_identifier` guaranteed not to collide with an existing
 * one. The column is `@unique`, so a collision would otherwise surface as a
 * raw constraint-violation error from the write instead of a clean retry.
 *
 * Takes the lookup as a callback rather than importing Prisma directly here
 * so this module — otherwise pure — stays usable from anywhere that already
 * has its own Prisma client in hand (authenticated share toggling, anonymous
 * resume creation, ...) without pulling in a second client or an import
 * cycle.
 */
export async function generateUniqueSharedIdentifier(
  findExisting: (token: string) => Promise<{ id: string } | null>
): Promise<string> {
  const maxAttempts = 8;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const token = createSharedIdentifier();
    const existing = await findExisting(token);

    if (!existing) {
      return token;
    }
  }

  throw new Error('Failed to generate unique shared identifier');
}

export function mapSharePayload(sharedIdentifier?: string | null): ResumeSharePayload {
  if (!sharedIdentifier) {
    return { enabled: false, path: null };
  }

  return {
    enabled: true,
    path: buildSharedResumePath(sharedIdentifier),
  };
}
